import { memo, useMemo } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGetApiServiceDetailQuery } from '../../../../redux/api';
import {
  formatConformanceSummary,
  formatLastRunTime,
  mapApiServiceDetailResponse,
  normalizeClusterDetailsHtml,
  resolveServiceAccount,
} from '../utils/apiServiceUtils';
import { K, apiLabel, getAdapterTypeLabel } from '../apiServiceI18n';
import ApiServiceStatusChip from './ApiServiceStatusChip';

function DetailField({ label, value, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      {children || (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

function ApiServiceDetailsDrawer({ target, onClose }) {
  const { t } = useTranslation();
  const { account: reduxAccount } = useSelector((state) => state.queryParams);

  const account = resolveServiceAccount(reduxAccount, target?.account);
  const wfgName = target?.workflowGroup ?? '';
  const wfName = target?.workflowName ?? '';

  const detailQuery = useGetApiServiceDetailQuery(
    { account, wfg_name: wfgName, wf_name: wfName },
    { skip: !target || !account || !wfgName || !wfName }
  );

  const { detail, error: mapError } = useMemo(
    () => mapApiServiceDetailResponse(detailQuery.data),
    [detailQuery.data]
  );

  const isLoading = detailQuery.isFetching;
  const emDash = t(K.API_SVC_EM_DASH);
  const serviceKey = target?.serviceKey || emDash;
  const clusterHtml =
    detail?.clusterDetails || normalizeClusterDetailsHtml(target?.details);

  return (
    <Drawer anchor="left" open={!!target} onClose={onClose}>
      <Box
        sx={{
          width: 920,
          maxWidth: '95vw',
          minWidth: 640,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f6f7fa',
        }}
      >
        <Box
          sx={{
            bgcolor: '#eceff3',
            borderBottom: '1px solid #d5d9e0',
            px: 1.25,
            py: 0.9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.1rem',
              color: '#5f6878',
              textTransform: 'uppercase',
              letterSpacing: 0.2,
            }}
          >
            {t(K.API_SVC_DETAILS_TITLE)}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t(K.API_SVC_CLOSE_DETAILS)}
            sx={{ color: '#6a7382', border: '1px solid #d2d8e2', borderRadius: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: '1px solid #d5d9e0',
            bgcolor: '#f8f9fc',
            color: '#4b5566',
          }}
        >
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b>{t(K.API_SVC_SERVICE_KEY)}:</b> {serviceKey}
          </Typography>
          <Typography variant="body2">
            <b>{apiLabel(K.ACCOUNT)}:</b> {account || emDash}
          </Typography>
          <Typography variant="body2">
            <b>{apiLabel(K.LBL_NAME)}:</b> {target?.name || detail?.name || emDash}
          </Typography>
          <Typography variant="body2">
            <b>{apiLabel(K.WORKFLOW_GROUP)}:</b> {wfgName || emDash}
          </Typography>
          <Typography variant="body2">
            <b>{apiLabel(K.WORKFLOW)}:</b> {wfName || emDash}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {(mapError || detailQuery.error) && !isLoading && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mapError ||
                detailQuery.error?.data?.message ||
                detailQuery.error?.error ||
                t(K.API_SVC_DETAILS_FAILED)}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {detail && (
                <Paper variant="outlined" sx={{ p: 2, borderColor: '#d5d9e0' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    {t(K.API_SVC_DETAILS_SUMMARY)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <DetailField label={apiLabel(K.LBL_STATUS)}>
                      <ApiServiceStatusChip
                        status={detail.enabled ? 'enabled' : 'disabled'}
                      />
                    </DetailField>
                    <DetailField
                      label={t(K.API_SVC_COL_ADAPTER)}
                      value={getAdapterTypeLabel(detail.adapter, t) || emDash}
                    />
                    <DetailField
                      label={t(K.API_SVC_COL_AUTHORIZATION)}
                      value={detail.authorization || emDash}
                    />
                    <DetailField
                      label={t(K.API_SVC_COL_VERSION)}
                      value={detail.version || emDash}
                    />
                    <DetailField
                      label={t(K.API_SVC_COL_LAST_EXECUTED)}
                      value={formatLastRunTime(detail.lastRunTime) || emDash}
                    />
                    <DetailField
                      label={t(K.API_SVC_REMAINING_COUNT)}
                      value={
                        detail.remaining != null && detail.remaining !== ''
                          ? String(detail.remaining)
                          : emDash
                      }
                    />
                    <DetailField
                      label={t(K.API_SVC_BASE_URL)}
                      value={detail.baseUrl || emDash}
                    />
                    <DetailField
                      label={t(K.API_SVC_CUSTOM_URL)}
                      value={detail.customUrl || emDash}
                    />
                    <DetailField
                      label={t(K.API_SVC_CONFORMANCE)}
                      value={formatConformanceSummary(detail, t)}
                    />
                    <DetailField
                      label={t(K.API_SVC_DESCRIPTION)}
                      value={detail.description || emDash}
                    />
                  </Box>
                  {detail.workflowComments ? (
                    <Box sx={{ mt: 2 }}>
                      <DetailField
                        label={t(K.API_SVC_WORKFLOW_COMMENTS)}
                        value={detail.workflowComments}
                      />
                    </Box>
                  ) : null}
                  {detail.customUrlParams?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        {t(K.API_SVC_ADDITIONAL_PARAMS)}
                      </Typography>
                      <Stack spacing={0.75}>
                        {detail.customUrlParams.map((row) => (
                          <Typography key={`${row.name}-${row.value}`} variant="body2">
                            <strong>{row.name || emDash}</strong>: {row.value || emDash}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Paper>
              )}

              <Paper variant="outlined" sx={{ p: 2, borderColor: '#d5d9e0' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {t(K.API_SVC_CLUSTER_DETAILS_TITLE)}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {clusterHtml ? (
                  <Box
                    sx={{
                      color: '#1f2937',
                      fontSize: '0.92rem',
                      lineHeight: 1.5,
                      '& table': { borderCollapse: 'collapse', width: '100%' },
                      '& td, & th': { border: '1px solid #d5d9e0', p: 0.75 },
                    }}
                    dangerouslySetInnerHTML={{ __html: clusterHtml }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t(K.API_SVC_CLUSTER_DETAILS_EMPTY)}
                  </Typography>
                )}
              </Paper>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default memo(ApiServiceDetailsDrawer);
